import type { OfferEvent } from "@tdelivr/api-client/generated/experience/driver-bff.js";

import {
  EventStreamReader,
  nextAfterClosure,
  readClosure,
  type NextAction,
  type StreamClosure,
} from "./sse.ts";
import type { StreamOpen } from "./offer-inbox.ts";

/**
 * One courier's connection to EXP-003's offer stream, with the reconnect contract honoured rather
 * than reinvented.
 *
 * `fetch` and a stream reader, not `EventSource`, and the reason is in `sse.ts`: `EventSource`
 * cannot send an `Authorization` header and this operation is `bearerAuth` only, so the browser's
 * own implementation cannot open this stream at all. What is below is therefore the four things ADR
 * 0024 chose server-sent events in order to inherit — the framing, `retry:`, `Last-Event-ID` and the
 * reconnect — implemented against that contract. R-29.
 *
 * ## The termination contract, in order
 *
 * 1. **The last event is always `stream-closed`.** It carries `reason`, `reconnectable`, `resume`
 *    and `retryAfterMs`. A connection that ends without one ended by dropping, which is a different
 *    thing and is treated differently below.
 * 2. **`reconnectable: false` is not retried on the timer.** It is false for `token_expired` and
 *    `authorization_revoked`. Retrying either at the retry interval is the reconnect loop the flag
 *    exists to prevent: every attempt would be refused until the token changed.
 * 3. **`token_expired` asks for a new token, then reopens once.** ADR 0024: "Get a new one, then
 *    reconnect." The authority already refreshes a token that is about to expire, so asking it for
 *    one is all this needs — and `MAX_ATTEMPTS_PER_TOKEN` stops it looping if the provider keeps
 *    handing back the same one.
 * 4. **`authorization_revoked` stops for good.** A realm change rather than an expiry, so a fresh
 *    token would carry the same claims and every attempt would be refused identically.
 * 5. **`resume` goes back as `Last-Event-ID`.** Every `offer` event's `id` is the whole watch set
 *    with the versions already sent, so nothing is replayed: EXP-003 admits an offer only when its
 *    version exceeds the last one sent for it.
 * 6. **`nothing_left_to_watch` stops.** Every watched offer reached a terminal state, and a
 *    connection that names nothing and can discover nothing is refused 400 rather than opened on
 *    silence. The application opens a new stream when it is told about a new offer, which is the
 *    discovery gap and not a retry problem. R-22.
 *
 * ## A dropped connection is not a closed one
 *
 * A courier riding into a tunnel produces a `fetch` whose body ends with no `stream-closed` and no
 * error. That is treated as reconnectable on the last `retry:` the stream asked for, and never as a
 * closure — because an invented closure could claim `reconnectable: false` and stop the application
 * for good over a lost signal.
 */

/** What the caller is told, as it happens. */
export interface OfferStreamHandlers {
  readonly onOpen: (open: StreamOpen) => void;
  readonly onOffer: (offer: OfferEvent) => void;
  /** The connection ended. `closure` is absent when it dropped rather than closed. */
  readonly onClosed: (input: {
    readonly closure?: StreamClosure;
    readonly action: NextAction;
  }) => void;
  /** The connection could not be opened at all: a refusal before any byte of the body. */
  readonly onRefused: (input: { readonly status: number; readonly body: string }) => void;
}

export interface OfferStreamOptions {
  readonly baseUrl: string;
  readonly tenantId: string;
  readonly driverId: string;
  /** The offers to open with. EXP-003 refuses a connection that names nothing and cannot discover. */
  readonly offers: readonly string[];
  /** Asked per attempt, so a renewed token is picked up without rebuilding anything. */
  readonly accessToken: () => Promise<string | undefined>;
  readonly handlers: OfferStreamHandlers;
  /** Aborted when the screen goes away. Ends the connection immediately and retries nothing. */
  readonly signal: AbortSignal;
  readonly fetch?: typeof globalThis.fetch;
  readonly sleep?: (milliseconds: number, signal: AbortSignal) => Promise<void>;
}

/**
 * The delay used when a connection drops and the stream never sent a `retry:`.
 *
 * EXP-003 writes `retry:` as the first line of every body, so this is reached only when the body had
 * no first line — a refusal, or a socket that closed before anything arrived. Three seconds is what
 * a browser's own `EventSource` defaults to, which makes this application's behaviour on that path
 * the same as the standard's rather than a number of its own.
 */
export const FALLBACK_RETRY_MS = 3_000;

function waitFor(milliseconds: number, signal: AbortSignal): Promise<void> {
  if (signal.aborted) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(finish, milliseconds);
    function finish(): void {
      clearTimeout(timer);
      signal.removeEventListener("abort", finish);
      resolve();
    }
    signal.addEventListener("abort", finish, { once: true });
  });
}

/**
 * Hold one connection open, reconnecting while the contract says to, until the signal aborts.
 *
 * Returns rather than throws on every path a connection can reach, including a courier putting their
 * phone in their pocket: a stream's end is a normal outcome, and code that had to catch an exception
 * to learn that would eventually log it as an error.
 */
export async function runOfferStream(options: OfferStreamOptions): Promise<void> {
  const call = options.fetch ?? globalThis.fetch.bind(globalThis);
  const sleep = options.sleep ?? waitFor;
  const { handlers, signal } = options;

  let lastEventId: string | undefined;
  let token = await options.accessToken();
  let attemptsOnThisToken = 0;

  while (!signal.aborted) {
    attemptsOnThisToken += 1;

    const url = new URL(
      `v1/tenants/${encodeURIComponent(options.tenantId)}/driver-app/drivers/${encodeURIComponent(options.driverId)}/offer-stream`,
      ensureTrailingSlash(options.baseUrl),
    );
    if (options.offers.length > 0) url.searchParams.set("offers", options.offers.join(","));

    const headers: Record<string, string> = { accept: "text/event-stream" };
    if (token !== undefined) headers["authorization"] = `Bearer ${token}`;
    /*
     * `Last-Event-ID` set on the request rather than left to the browser.
     *
     * A browser sets it only on a reconnect it made itself, and this application is making the
     * reconnect. EXP-003 reads it as the watch set with the versions already sent, so sending it is
     * what makes a reconnect replay nothing.
     */
    if (lastEventId !== undefined) headers["last-event-id"] = lastEventId;

    let response: Response;
    try {
      response = await call(url.toString(), { headers, signal });
    } catch {
      // Never reached the service: no DNS, no socket, or the signal aborted. Both are the same to a
      // courier, and only one of them is worth retrying.
      if (signal.aborted) return;
      handlers.onClosed({ action: { kind: "reconnect", afterMs: FALLBACK_RETRY_MS } });
      await sleep(FALLBACK_RETRY_MS, signal);
      continue;
    }

    if (!response.ok || response.body === null) {
      /*
       * A refusal, which arrives as a status rather than as an event.
       *
       * EXP-003 checks the scope, the tenant and the identifiers before a byte is written, and that
       * ordering is what makes a refusal testable at all: once a 200 and a `text/event-stream`
       * header have gone out, a refusal can only be an event, and an event is not something an HTTP
       * layer acts on. So 400, 401 and 403 all land here and none of them is retried — a 400 means
       * this connection has nothing it could ever watch, and the other two mean the token is wrong.
       */
      const body = await response.text().catch(() => "");
      handlers.onRefused({ status: response.status, body });
      return;
    }

    const reader = new EventStreamReader();
    const decoder = new TextDecoder();
    const bytes = response.body.getReader();
    let closure: StreamClosure | undefined;

    try {
      for (;;) {
        const chunk = await bytes.read();
        if (chunk.done) break;
        for (const event of reader.push(decoder.decode(chunk.value, { stream: true }))) {
          if (event.id !== undefined) lastEventId = event.id;
          if (event.name === "stream-open") {
            const open = readOpen(event.data);
            if (open !== undefined) handlers.onOpen(open);
            continue;
          }
          if (event.name === "offer") {
            const offer = readOffer(event.data);
            if (offer !== undefined) handlers.onOffer(offer);
            continue;
          }
          if (event.name === "stream-closed") {
            closure = readClosure(parse(event.data));
            // The last event, every time. Nothing follows it, so nothing is read after it.
            break;
          }
          // An event name this build does not know is ignored, which is how the contract can grow.
        }
        if (closure !== undefined) break;
      }
    } catch {
      // The body failed mid-stream. Same as a drop below.
    } finally {
      await bytes.cancel().catch(() => undefined);
    }

    if (signal.aborted) return;

    if (closure === undefined) {
      /*
       * The body ended with no `stream-closed`: the connection dropped.
       *
       * Reconnectable on the last `retry:` the stream asked for, and deliberately **not** turned
       * into a closure — an invented one could claim `reconnectable: false` and stop a working
       * application over a tunnel.
       */
      const afterMs = reader.retryMs ?? FALLBACK_RETRY_MS;
      handlers.onClosed({ action: { kind: "reconnect", afterMs, ...idOf(lastEventId) } });
      await sleep(afterMs, signal);
      continue;
    }

    const action = nextAfterClosure({
      closure,
      retryMs: reader.retryMs,
      attemptsOnThisToken,
    });
    handlers.onClosed({ closure, action });

    if (action.kind === "stop") return;

    if (action.kind === "renew_then_reconnect") {
      /*
       * A new token first, and only then another attempt.
       *
       * `accessToken()` refreshes one that is about to expire before handing it over, so this is a
       * request for a fresh credential rather than a re-read of the stale one. The attempt counter
       * resets only when the token actually changed: a provider returning the same expired value
       * would otherwise reset it every time and loop for ever, which is the exact failure
       * `reconnectable: false` is published to prevent.
       */
      const renewed = await options.accessToken();
      if (renewed !== token) {
        token = renewed;
        attemptsOnThisToken = 0;
      }
      lastEventId = action.lastEventId ?? lastEventId;
      continue;
    }

    lastEventId = action.lastEventId ?? lastEventId;
    await sleep(action.afterMs, signal);
  }
}

function idOf(lastEventId: string | undefined): { readonly lastEventId?: string } {
  return lastEventId === undefined ? {} : { lastEventId };
}

function ensureTrailingSlash(base: string): string {
  return base.endsWith("/") ? base : `${base}/`;
}

function parse(data: string): unknown {
  try {
    return JSON.parse(data);
  } catch {
    return undefined;
  }
}

/**
 * Read `stream-open`, which says what the connection can and cannot do.
 *
 * `discovery` is the field that matters and is why this is read at all rather than ignored: a
 * courier's own token cannot reach DSP-02's listing, so `discovery: false` means this connection
 * will never find an offer by itself. An application that knows it can say so; one shown an empty
 * stream cannot tell it from "no offers came in". R-22.
 */
export function readOpen(data: string): StreamOpen | undefined {
  const value = parse(data);
  if (typeof value !== "object" || value === null) return undefined;
  const candidate = value as Record<string, unknown>;
  if (typeof candidate["discovery"] !== "boolean") return undefined;
  const watching = Array.isArray(candidate["watching"])
    ? candidate["watching"].filter((entry): entry is string => typeof entry === "string")
    : [];
  return {
    discovery: candidate["discovery"],
    watching,
    pollIntervalMs: numberOr(candidate["pollIntervalMs"], 0),
    maxLifetimeMs: numberOr(candidate["maxLifetimeMs"], 0),
    cursorWasUnreadable: candidate["cursorWasUnreadable"] === true,
  };
}

/**
 * Read an `offer` event, refusing anything that is not one.
 *
 * The eight fields the contract names, checked rather than cast. `expiresAt` and `createdAt` are the
 * two the countdown runs on, so an event missing either is dropped — a ring drawn over `NaN` draws
 * nothing and a courier would see a blank hero over a live offer.
 */
export function readOffer(data: string): OfferEvent | undefined {
  const value = parse(data);
  if (typeof value !== "object" || value === null) return undefined;
  const candidate = value as Record<string, unknown>;
  const statuses = [
    "created",
    "delivered",
    "viewed",
    "accepted",
    "rejected",
    "expired",
    "cancelled",
  ] as const;
  const status = statuses.find((known) => known === candidate["status"]);
  if (
    status === undefined ||
    typeof candidate["offerId"] !== "string" ||
    typeof candidate["version"] !== "number" ||
    typeof candidate["expiresAt"] !== "string" ||
    typeof candidate["createdAt"] !== "string" ||
    typeof candidate["rideRequestId"] !== "string" ||
    typeof candidate["rank"] !== "number" ||
    typeof candidate["terminal"] !== "boolean"
  ) {
    return undefined;
  }
  if (!Number.isFinite(Date.parse(candidate["expiresAt"]))) return undefined;
  if (!Number.isFinite(Date.parse(candidate["createdAt"]))) return undefined;
  return {
    offerId: candidate["offerId"],
    status,
    version: candidate["version"],
    expiresAt: candidate["expiresAt"],
    createdAt: candidate["createdAt"],
    rideRequestId: candidate["rideRequestId"],
    rank: candidate["rank"],
    terminal: candidate["terminal"],
  };
}

function numberOr(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
